import { useEffect, useId, useRef } from "react";
import { useHudStore } from "../store/hudStore";
import { DOMAINS, heatColor, stepShare, fmtDate, fmtDay, isoToday, titleCase } from "../data/seed";
import { parseStep, stepStatus, activeSteps, nextCycle } from "../lib/cadence";
import { Badge, Button, Menu, Progress } from "../design-system/components";
import EditQuestForm from "./EditQuestForm";
import type { Quest } from "../types";
import "./questRows.css";

// The board uses the same layout as the component gallery, with additional
// slots for the real checklist, cadence metadata, and inline editor.
export default function QuestRow({ quest: q }: { quest: Quest }) {
  const st = useHudStore();
  const done = !!st.done[q.id];
  const skipped = !!st.skipped[q.id];
  const d = DOMAINS.find((x) => x.name === q.domain);
  const main = st.mains.find((m) => m.id === q.main);
  const color = heatColor(d ? d.health : 60);
  const hasSteps = !!(q.steps && q.steps.length);
  const expanded = !!st.expanded[q.id];
  const editing = st.editingId === q.id;
  const steps = q.steps || [];
  const today = isoToday();
  const month = today.slice(0, 7);
  // Only steps in their month count. A quarterly premium sits out the months
  // between, dimmed, and the checklist can finish without it.
  const active = activeSteps(steps, month);
  const stepsDone = active.filter((i) => done || st.stepDone[q.id + ":" + i]).length;
  const stepPct = (stepsDone / Math.max(1, active.length)) * 100;
  const nextOn = active.length === 0 ? nextCycle(steps, month) : "";
  const overdue = !!q.due && !done && q.due < today;
  const rowId = useId();
  const titleId = `${rowId}-title`;
  const checklistId = `${rowId}-steps`;
  const actionsRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const wasEditingRef = useRef(false);
  const editReturnRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (editing && !wasEditingRef.current) detailsRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    if (!editing && wasEditingRef.current && st.editingId === null && editReturnRef.current?.isConnected) editReturnRef.current.focus();
    wasEditingRef.current = editing;
  }, [editing, st.editingId]);

  const menuItems = [
    { label: "Quest details", onSelect: () => st.openQuest(q.id) },
    { label: "Edit quest", onSelect: () => {
      editReturnRef.current = actionsRef.current?.querySelector<HTMLElement>("summary") || null;
      st.startEdit(q.id);
    } },
    ...(q.type.toLowerCase() === "daily" && !done
      ? [{ label: skipped ? "Show today" : "Skip today", onSelect: () => st.skip(q.id) }]
      : []),
  ];

  return (
    <article className={`ds-quest hud-quest${done ? " ds-quest--done" : ""}${skipped ? " hud-quest--skipped" : ""}`} aria-labelledby={titleId}>
      <button type="button" className="ds-quest-complete ds-focusable" onClick={() => done ? st.undo(q.id) : st.complete(q.id)} aria-label={`${done ? "Reopen" : "Complete"} ${q.title}`} aria-pressed={done}>
        <span className="ds-quest-check"><span className="ds-icon" aria-hidden="true"><i className="fa-solid fa-check" /></span></span>
      </button>

      <div className="ds-quest-heading">
        <h3 id={titleId} className="ds-quest-title"><button type="button" onClick={() => st.openQuest(q.id)} title="Open quest details" className="ds-quest-open ds-focusable">{q.title}</button></h3>
        <Badge>{titleCase(q.type)}</Badge>
      </div>

      <div className="ds-quest-metadata hud-quest-metadata" data-quest-chips>
        <button type="button" className="hud-quest-meta-link ds-focusable" onClick={() => st.openMain(q.main || "m1")} style={{ color: main?.color || "var(--mut)" }}>
          <span className="ds-icon" aria-hidden="true"><i className={main?.fa || "fa-solid fa-circle-question"} /></span>
          <span>{main?.name || "Unassigned"}</span>
        </button>
        <button type="button" className="hud-quest-meta-link ds-focusable" onClick={() => st.openDomain(q.domain)}>
          <span className="hud-quest-domain-dot" style={{ background: color }} aria-hidden="true" />
          <span>{q.domain}</span>
        </button>
        {q.days === "weekdays" && (
          <span className="hud-quest-readout" title="Rests on Saturday and Sunday"><span className="ds-icon" aria-hidden="true"><i className="fa-solid fa-briefcase" /></span>Weekdays</span>
        )}
        {skipped && <Badge>Skipped today</Badge>}
        {q.due && (
          <Button size="compact" variant={overdue ? "danger" : "quiet"} icon="fa-regular fa-calendar" className="hud-quest-date" onClick={() => st.openQuest(q.id)}>
            {overdue ? "Overdue · " : ""}{fmtDate(q.due)}
          </Button>
        )}
        {hasSteps && (
          <Button size="compact" className="hud-quest-step-toggle" onClick={() => st.toggleExpand(q.id)} aria-expanded={expanded} aria-controls={expanded ? checklistId : undefined} icon={expanded ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"}>
            <span className="hud-quest-step-toggle-content">
              <span>{active.length > 0 ? `${stepsDone} / ${active.length} steps` : `${steps.length} step${steps.length === 1 ? "" : "s"} from ${fmtDate(nextOn)}`}</span>
              {active.length > 0 && <Progress value={stepPct} className="hud-quest-step-progress" aria-label={`Checklist progress for ${q.title}`} />}
            </span>
          </Button>
        )}
      </div>

      <div className="ds-quest-footer" ref={actionsRef}>
        <div className="ds-quest-actions" role="group" aria-label={`Actions for ${q.title}`}>
          <Button size="compact" variant={q.pinned ? "primary" : "secondary"} icon="fa-solid fa-thumbtack" onClick={() => st.pin(q.id)} aria-label={`Pin ${q.title} for today`} aria-pressed={q.pinned} title="Pin for today" />
          <Button size="compact" icon="fa-solid fa-stopwatch" onClick={() => st.focus(q.id)} aria-label={`Start focus on ${q.title}`} title="Start focus" />
          <Menu ariaLabel={`More actions for ${q.title}`} items={menuItems} />
        </div>
        <span className="ds-quest-xp">+{q.xp} XP</span>
      </div>

      {(hasSteps && expanded || editing) && <div className="hud-quest-details" ref={detailsRef}>
        {hasSteps && expanded && (
          <div className="hud-quest-steps" id={checklistId} role="group" aria-label={`Steps for ${q.title}`}>
            {steps.map((text, i) => {
              const { label, recur } = parseStep(text);
              // The date is this month's occurrence or the next occurrence for
              // a step outside its month.
              const { active: inMonth, on } = stepStatus(text, month);
              const sd = inMonth && (done || !!st.stepDone[q.id + ":" + i]);
              const late = inMonth && !sd && !!on && on < today;
              return (
                <button key={i} type="button" className={`hud-quest-step ds-focusable${sd ? " hud-quest-step--done" : ""}`} onClick={() => st.toggleStep(q.id, i)} aria-pressed={sd} disabled={!inMonth} title={inMonth ? undefined : "Not due this month. Next on " + fmtDate(on)}>
                  <span className="hud-quest-step-check" aria-hidden="true"><i className="fa-solid fa-check" /></span>
                  <span className="hud-quest-step-title">{label}</span>
                  <span className="hud-quest-step-meta">
                    {recur && <Badge className="hud-quest-recurrence" title={"Repeats " + recur}><i aria-hidden="true" className="fa-solid fa-repeat" />{recur}</Badge>}
                    {on && <Badge tone={late ? "danger" : "neutral"}><i aria-hidden="true" className="fa-solid fa-calendar-day" />{inMonth ? "" : "Next "}{fmtDay(on)}</Badge>}
                    {inMonth && <span className="hud-quest-step-xp">+{stepShare(q, active, i)} XP</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {editing && <EditQuestForm quest={q} />}
      </div>}
    </article>
  );
}
